import type { StrokeData } from "../site/bishun-drawing.js";
import type { WorksheetGrid, WorksheetInk } from "./worksheet-state.js";
import {
  type Point,
  labelSize,
  placeLabels,
  strokeArrow,
  type StrokeArrow,
} from "./worksheet-stroke-arrows.js";

/**
 * The squares on a sheet, and the characters drawn in them.
 *
 * Every character is drawn from its stroke outlines, the same Make Me a Hanzi
 * data the 笔顺 figure animates, so the shape a learner traces is the shape
 * the stroke order is drawn from. A font would give a different hand.
 *
 * A sheet of a hundred characters draws each of them a dozen times, so each
 * stroke is written into the page once, in a library of definitions, and
 * every square refers to it with `<use>`. The stroke order row needs the
 * strokes written so far in grey and the next one in black, and that is one
 * cumulative group per stroke, each the one before it plus one stroke. Every
 * square is then two references, whatever the stroke count.
 *
 * A row of squares is one SVG rather than one per square, on a grid of 1024
 * units a square, which is the grid the outlines are drawn on.
 */

const svgNamespace = "http://www.w3.org/2000/svg";

/** One square of the outlines' own grid. */
const unit = 1024;

/** Turns the outlines' upside-down grid the right way up. See `bishun-drawing.ts`. */
const upright = "scale(1, -1) translate(0, -900)";

/** The ink for the model and the stroke being written. */
export const blackInk = "#000000";

/** The ink for the strokes already written in the stroke order row. */
export const writtenInk = "#a6a6a6";

/**
 * The inks a tracing square can be printed in.
 *
 * Red, the pale red of 描红, is the default. It is what a 描红 exercise book
 * prints, and it is told apart from the black model at a glance. Grey is
 * under More settings, for a printer with no colour.
 */
export const traceInks: Readonly<Record<WorksheetInk, string>> = {
  red: "#f4a7a7",
  grey: "#cfcfcf",
};

/** The ink a tracing square is printed in when nothing else is chosen. */
export const traceInk = traceInks.red;

/** The square's border and the guides inside it. */
const borderInk = "#7a7a7a";
const guideInk = "#b8b8b8";

/**
 * How a row's 米字格 guides are drawn, in millimetres on paper.
 *
 * A practice square is there to be written in, and its guides are drawn to
 * be seen. The stroke order row's squares are about half the size and carry a
 * stroke already, where the same guides crossed the stroke being shown and
 * read as part of it. They take a finer, paler, shorter-dashed line.
 */
export const guideStyles = {
  practice: { ink: guideInk, width: 0.2, dash: 1, border: borderInk },
  fine: { ink: "#d9d9d9", width: 0.1, dash: 0.5, border: borderInk },
  /**
   * The faint red of a 描红 exercise book, guides and border both, for the
   * squares the look-alike and vocabulary sheets are answered in.
   */
  tracing: { ink: "#f6c4c1", width: 0.2, dash: 1, border: "#ef9f9a" },
} as const;

/** One of the ways a row's guides are drawn. */
export type GuideStyle = keyof typeof guideStyles;

function element(
  document: Document,
  name: string,
  attributes: Readonly<Record<string, string>>,
): SVGElement {
  const made = document.createElementNS(svgNamespace, name);
  for (const [attribute, value] of Object.entries(attributes)) {
    made.setAttribute(attribute, value);
  }

  return made;
}

/** A point as a path command writes it. */
function pathPoint([x, y]: Point): string {
  return `${x.toFixed(0)} ${y.toFixed(0)}`;
}

/** What one square holds. */
export type SquareContent =
  | { readonly kind: "empty" }
  | { readonly kind: "glyph"; readonly character: string; readonly ink: string }
  | {
      readonly kind: "stroke";
      readonly character: string;
      /** The stroke being written, from 0. */
      readonly stroke: number;
    }
  | { readonly kind: "numbered"; readonly character: string };

/** What the library knows about one character it holds. */
interface Held {
  readonly id: string;
  readonly data: StrokeData | undefined;
}

/** What a library needs to draw with. */
export interface GlyphLibraryProperties {
  readonly document: Document;
}

/**
 * The definitions every square on a sheet draws from, and the squares.
 *
 * A character with no outlines is drawn as text in the site's own Chinese
 * face instead, which still gives a model to copy and something to trace, but
 * has no stroke order to show.
 */
export class GlyphLibrary {
  readonly #document: Document;
  readonly #held = new Map<string, Held>();

  /** The hidden SVG the definitions live in. Put it in the page once. */
  readonly element: SVGElement;
  readonly #defs: SVGElement;

  constructor(properties: GlyphLibraryProperties) {
    this.#document = properties.document;
    this.element = element(this.#document, "svg", {
      class: "cb-ws-library",
      width: "0",
      height: "0",
      "aria-hidden": "true",
      focusable: "false",
    });
    this.#defs = element(this.#document, "defs", {});
    this.element.append(this.#defs);
  }

  /**
   * Write a character's strokes into the library, once.
   * @param data - Its outlines, or nothing to draw it in the font.
   */
  hold(character: string, data: StrokeData | undefined): void {
    if (this.#held.has(character)) {
      return;
    }
    const id = `cb-ws-${(character.codePointAt(0) ?? 0).toString(16)}`;
    this.#held.set(character, { id, data });
    if (data === undefined) {
      return;
    }

    for (const [at, stroke] of data.strokes.entries()) {
      this.#defs.append(
        element(this.#document, "path", {
          id: `${id}-s${String(at)}`,
          d: stroke,
        }),
      );
      // The strokes up to and including this one.
      const upTo = element(this.#document, "g", { id: `${id}-u${String(at)}` });
      if (at > 0) {
        upTo.append(this.#use(`${id}-u${String(at - 1)}`, {}));
      }
      upTo.append(this.#use(`${id}-s${String(at)}`, {}));
      this.#defs.append(upTo);
    }
  }

  /** How many strokes a held character has, or nothing for one with no outlines. */
  strokeCount(character: string): number | undefined {
    return this.#held.get(character)?.data?.strokes.length;
  }

  /**
   * A row of squares.
   * @param size - The side of a square, in millimetres.
   * @param squares - What each square holds, left to right.
   * @param label - What a screen reader is told the row is, if anything.
   * @param guides - How the guides are drawn.
   * @param grid - Which guides: a 米字格's cross and diagonals, a 田字格's
   * cross, or none.
   */
  row(
    size: number,
    squares: readonly SquareContent[],
    label?: string,
    guides: GuideStyle = "practice",
    grid: WorksheetGrid = "mi",
  ): SVGElement {
    const count = squares.length;
    const row = element(this.#document, "svg", {
      class: "cb-ws-row",
      viewBox: `0 0 ${String(count * unit)} ${String(unit)}`,
      width: `${String(count * size)}mm`,
      height: `${String(size)}mm`,
      ...(label === undefined
        ? { "aria-hidden": "true" }
        : { role: "img", "aria-label": label }),
    });
    // A line a fixed width on paper, whatever size the square is printed at.
    const perMm = unit / size;

    if (grid !== "plain") {
      row.append(this.#guides(count, perMm, guideStyles[guides], grid));
    }
    for (const [at, square] of squares.entries()) {
      const drawn = this.#square(square);
      if (drawn !== undefined) {
        drawn.setAttribute("transform", `translate(${String(at * unit)}, 0)`);
        row.append(drawn);
      }
    }
    row.append(this.#borders(count, perMm, guideStyles[guides].border));

    return row;
  }

  #use(id: string, attributes: Readonly<Record<string, string>>): SVGElement {
    return element(this.#document, "use", { href: `#${id}`, ...attributes });
  }

  #square(square: SquareContent): SVGElement | undefined {
    if (square.kind === "empty") {
      return undefined;
    }
    const held = this.#held.get(square.character);
    if (held === undefined) {
      return undefined;
    }
    const group = element(this.#document, "g", {});

    if (held.data === undefined) {
      if (square.kind === "glyph" || square.kind === "numbered") {
        const text = element(this.#document, "text", {
          class: "cb-ws-font-glyph",
          x: String(unit / 2),
          y: String(unit * 0.84),
          "text-anchor": "middle",
          "font-size": String(unit * 0.86),
          fill: square.kind === "glyph" ? square.ink : blackInk,
        });
        text.textContent = square.character;
        group.append(text);
      }

      return group;
    }

    const strokes = held.data.strokes.length;
    const drawn = element(this.#document, "g", { transform: upright });
    group.append(drawn);

    if (square.kind === "glyph") {
      drawn.append(
        this.#use(`${held.id}-u${String(strokes - 1)}`, { fill: square.ink }),
      );
    } else if (square.kind === "stroke") {
      if (square.stroke > 0) {
        drawn.append(
          this.#use(`${held.id}-u${String(square.stroke - 1)}`, {
            fill: writtenInk,
          }),
        );
      }
      drawn.append(
        this.#use(`${held.id}-s${String(square.stroke)}`, { fill: blackInk }),
      );
    } else {
      // Grey, so the black arrows and numbers over it stand out.
      drawn.append(
        this.#use(`${held.id}-u${String(strokes - 1)}`, { fill: writtenInk }),
      );
      group.append(this.#numbers(held.data));
    }

    return group;
  }

  /**
   * Each stroke's number and an arrow showing which way it is written, for
   * the stroke order pages. `worksheet-stroke-arrows.ts` places them.
   *
   * The arrows run inside the strokes, thin enough to leave the grey ink
   * around them. The numbers are sized by the stroke count and have a white
   * edge, so one that crosses a stroke can still be read.
   */
  #numbers(data: StrokeData): SVGElement {
    const numbers = element(this.#document, "g", { class: "cb-ws-numbers" });
    const arrows: string[] = [];

    const size = labelSize(data.medians.length);
    const drawn = placeLabels(
      data.medians.flatMap((median, at) => {
        const arrow = strokeArrow(median);

        return arrow === undefined ? [] : [{ ...arrow, at }];
      }),
      size,
    ) as readonly (StrokeArrow & { readonly at: number })[];
    for (const { at, ...arrow } of drawn) {
      const head = arrow.shaft.at(-1)!;
      arrows.push(
        `M${arrow.shaft.map((point) => pathPoint(point)).join("L")}`,
        `M${pathPoint(arrow.barbs[0])}L${pathPoint(head)}L${pathPoint(arrow.barbs[1])}`,
      );
      const label = element(this.#document, "text", {
        x: arrow.label[0].toFixed(0),
        y: (arrow.label[1] + size * 0.35).toFixed(0),
        "text-anchor": "middle",
        "font-size": String(size),
        "font-weight": "500",
        "font-family": "sans-serif",
        fill: blackInk,
        stroke: "#ffffff",
        "stroke-width": String(Math.round(size * 0.22)),
        "paint-order": "stroke",
      });
      label.textContent = String(at + 1);
      numbers.append(label);
    }
    numbers.prepend(
      element(this.#document, "path", {
        d: arrows.join(""),
        stroke: blackInk,
        "stroke-width": "7",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        fill: "none",
      }),
    );

    return numbers;
  }

  /**
   * The dashed guides in every square: a cross, and for a 米字格 both
   * diagonals as well, which help place a 撇 or a 捺.
   */
  #guides(
    count: number,
    perMm: number,
    style: (typeof guideStyles)[GuideStyle],
    grid: Exclude<WorksheetGrid, "plain">,
  ): SVGElement {
    const dash = style.dash * perMm;
    const guides = element(this.#document, "g", {
      stroke: style.ink,
      "stroke-width": String(style.width * perMm),
      "stroke-dasharray": `${String(dash)} ${String(dash)}`,
      fill: "none",
    });
    const half = unit / 2;
    const width = count * unit;
    const lines: string[] = [`M0 ${String(half)}H${String(width)}`];

    for (let at = 0; at < count; at += 1) {
      const left = at * unit;
      lines.push(`M${String(left + half)} 0V${String(unit)}`);
      if (grid === "mi") {
        lines.push(
          `M${String(left)} 0L${String(left + unit)} ${String(unit)}`,
          `M${String(left + unit)} 0L${String(left)} ${String(unit)}`,
        );
      }
    }
    guides.append(element(this.#document, "path", { d: lines.join("") }));

    return guides;
  }

  #borders(count: number, perMm: number, ink: string): SVGElement {
    const weight = 0.3 * perMm;
    const inset = weight / 2;
    const width = count * unit;
    const lines = [
      `M${String(inset)} ${String(inset)}H${String(width - inset)}V${String(unit - inset)}H${String(inset)}Z`,
    ];
    for (let at = 1; at < count; at += 1) {
      lines.push(`M${String(at * unit)} 0V${String(unit)}`);
    }

    return element(this.#document, "path", {
      d: lines.join(""),
      stroke: ink,
      "stroke-width": String(weight),
      fill: "none",
    });
  }
}
